import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import CardTypeSection from '@/sections/sell/CardTypeSection';
import CardSellInfoSection from '@/sections/sell/CardSellInfoSection';
import PriceEstimateSection from '@/sections/sell/PriceEstimateSection';
import SuccessSection from '@/sections/sell/SuccessSection';
import ImagePreviewWithRequest from '@/components/ImagePreviewWithRequest';
import ConfirmDialog from '@/components/ConfirmDialog';
import BindPhoneDialog from '@/components/BindPhoneDialog';
import AddressEditor from '@/components/AddressEditor';
import PenaltyRestrictionDialog from '@/components/PenaltyRestrictionDialog';
import { getCardInfo, getCardStats } from '@/api/card';
import { createMyListing, updateMyListing, getListing, cancelListing } from '@/api/listing';
import { getMyProfile } from '@/api/profile';
import { getShippingAddresses, createShippingAddress } from '@/api/address';
import { validateImage, getUploadUrl, uploadFile } from '@/utils/imageUtils';
import { trackEvent } from '@/utils/googleAnalytics';
import usePenaltyGuard from '@/hooks/usePenaltyGuard';
import { USER_STATUS } from '@/utils/userStatus';

const CreateListingPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id, listingId } = useParams();
  const location = useLocation();
  const queryClient = useQueryClient();
  const handleRestrictionSecondary = useCallback(() => {
    navigate('/');
  }, [navigate]);
  const {
    ensureActionAllowed,
    penaltyDialogProps,
    status: penaltyStatus,
    isPenaltyStatusFetched,
    isPenaltyStatusLoading,
  } = usePenaltyGuard({ onSecondaryAction: handleRestrictionSecondary });
  const isNonPSA = new URLSearchParams(location.search).get('NON_PSA') !== null;
  const shouldFocusPrice = new URLSearchParams(location.search).get('focus') === 'price';
  const [isPriceAdjustMode, setIsPriceAdjustMode] = useState(shouldFocusPrice);
  const [isSuccess, setIsSuccess] = useState(false);
  const [listingData, setListingData] = useState(null);
  const isEditMode = !!listingId;
  const [isPhoneDialogOpen, setIsPhoneDialogOpen] = useState(false);
  const [phoneDialogCallback, setPhoneDialogCallback] = useState(null);

  const restrictionHandledRef = useRef(false);
  
  // Address editor state
  const [isAddressEditorOpen, setIsAddressEditorOpen] = useState(false);
  const [addressEditorCallback, setAddressEditorCallback] = useState(null);
  
  // Reference for price input
  const priceInputRef = useRef(null);
  
  // Confirm dialog state
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false);
  const [listingToCancel, setListingToCancel] = useState(null);
  
  // Preview dialog state
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState(null);
  
  // Product not found dialog state
  const [isProductNotFoundDialogOpen, setIsProductNotFoundDialogOpen] = useState(false);
  
  // Form state
  const [grade, setGrade] = useState('PSA_10');
  const [condition, setCondition] = useState('MINT');
  const [description, setDescription] = useState('');
  const [price, setPrice] = useState(0);
  const [quantity, setQuantity] = useState(1);
  const [images, setImages] = useState({
    front: null,
    back: null,
    frontTopLeft: null,
    frontTopRight: null,
    frontBottomLeft: null,
    frontBottomRight: null,
    damage: null,
    extras: []
  });
  const [internalCardType, setInternalCardType] = useState('');

  // Get user profile to check phone verification
  const { data: userProfile, isLoading: isLoadingProfile } = useQuery({
    queryKey: ['user-profile'],
    queryFn: getMyProfile,
  });

  // Get user addresses to check if user has any addresses
  const { data: userAddresses, isLoading: isLoadingAddresses } = useQuery({
    queryKey: ['user-addresses'],
    queryFn: getShippingAddresses,
  });

  useEffect(() => {
    if (!isPenaltyStatusFetched || isPenaltyStatusLoading) {
      return;
    }

    if (penaltyStatus === USER_STATUS.NORMAL) {
      restrictionHandledRef.current = false;
      return;
    }

    if (restrictionHandledRef.current) {
      return;
    }

    restrictionHandledRef.current = true;
    ensureActionAllowed();
  }, [ensureActionAllowed, isPenaltyStatusFetched, isPenaltyStatusLoading, penaltyStatus]);

  // Image indexes to keys mapping
  const imageIndexToKey = [
    'front',
    'back',
    'frontTopLeft', 
    'frontTopRight',
    'frontBottomLeft',
    'frontBottomRight',
    'damage'
  ];

  // Fetch existing listing data if in edit mode
  const { data: existingListing, isLoading: isLoadingListing } = useQuery({
    queryKey: ['listing', listingId],
    queryFn: () => getListing(listingId),
    enabled: isEditMode
  });

  // Populate form with existing listing data when available
  useEffect(() => {
    if (existingListing?.data && isEditMode) {
      const listing = existingListing.data;
      // Pre-populate form with existing data
      setPrice(listing.price);
      if (listing.psa) {
        setGrade(listing.psa);
      } else if (listing.condition) {
        setCondition(listing.condition);
        setDescription(listing.description || '');
      } else if (listing.bundledQuantity) {
        setQuantity(listing.bundledQuantity);
      }
      setInternalCardType(listing.type);
      
      // If we have images, set them
      if (listing.images) {
        const newImages = {
          front: null,
          back: null,
          frontTopLeft: null,
          frontTopRight: null,
          frontBottomLeft: null,
          frontBottomRight: null,
          damage: null,
          extras: []
        };
        
        // Set main images
        Object.keys(listing.images).forEach(key => {
          if (key !== 'extras') {
            newImages[key] = listing.images[key];
          }
        });
        
        // Set extra images
        if (listing.images.extras && Array.isArray(listing.images.extras)) {
          newImages.extras = [...listing.images.extras];
        }
        
        setImages(newImages);
      }
    }
  }, [existingListing, isEditMode]);

  // Fetch card data, only for new listing
  const { data: cardInfo, isLoading: isLoadingCard } = useQuery({
    queryKey: ['card', id],
    queryFn: () => getCardInfo(id),
    enabled: !!id
  });

  // Fetch card stats for pricing info
  const { data: cardStats, isLoading: isLoadingStats } = useQuery({
    queryKey: ['cardStats', id || (existingListing?.data?.product?.id)],
    queryFn: () => getCardStats(id || existingListing?.data?.product?.id),
    enabled: !!id || !!(existingListing?.data?.product?.id)
  });

  // Determine card type based on API response and URL parameters
  useEffect(() => {
    if (cardInfo?.data && !isEditMode) {
      const apiCardType = cardInfo.data.type;
      
      if (apiCardType === 'CARD_BOX') {
        setInternalCardType('CARD_BOX');
      } else if (apiCardType === 'SINGLE_CARD') {
        // For SINGLE cards, check if we should use NON_PSA based on URL parameter
        setInternalCardType(isNonPSA ? 'NON_PSA_SINGLE_CARD' : 'PSA_SINGLE_CARD');
      } else {
        // Default to PSA_SINGLE_CARD if unknown
        setInternalCardType('PSA_SINGLE_CARD');
      }
    }
  }, [cardInfo, isEditMode, isNonPSA]);

  // Create listing mutation
  const createListingMutation = useMutation({
    mutationFn: (listingData) => createMyListing(listingData)
  });

  // Update listing mutation
  const updateListingMutation = useMutation({
    mutationFn: (data) => updateMyListing(listingId, data)
  });

  // Cancel listing mutation
  const cancelListingMutation = useMutation({
    mutationFn: (listingId) => cancelListing(listingId),
    onSuccess: () => {
      // Clear listings page cached state and force refetch on listings page
      try {
        sessionStorage.removeItem('listings_page_state');
      } catch (e) {
        // ignore sessionStorage errors
        console.warn('Failed to clear listings_page_state cache', e);
      }
      queryClient.invalidateQueries({ queryKey: ['listings'] });
      navigate('/me/listings');
      // try {
      //   // Try to go back to previous page
      //   if (window.history.length > 2) {
      //     navigate(-1);
      //   } else {
      //     // Fallback to my listings page if no history
      //     navigate('/my/listings');
      //   }
      // } catch (error) {
      //   // Fallback if navigation fails
      //   navigate('/my/listings');
      // }
    }
  });

  // Handle mutation results
  useEffect(() => {
    if (createListingMutation.isSuccess && !isEditMode) {
      setListingData(createListingMutation.data?.data);
      setIsSuccess(true);
      trackEvent('user_listed', {
        amount: price,
      });
    } else if (updateListingMutation.isSuccess) {
      setListingData(updateListingMutation.data?.data?.data);
      setIsSuccess(true);
      trackEvent('user_listed', {
        amount: price,
      });
    }
  }, [createListingMutation.isSuccess, updateListingMutation.isSuccess, createListingMutation.data, updateListingMutation.data]);

  // Handle mutation errors
  useEffect(() => {
    if (createListingMutation.isError) {
      console.error('Error creating listing:', createListingMutation.error);
      // Handle error (could add toast notification here)
    } else if (updateListingMutation.isError) {
      console.error('Error updating listing:', updateListingMutation.error);
      // Handle error (could add toast notification here)
    }
  }, [createListingMutation.isError, updateListingMutation.isError, createListingMutation.error, updateListingMutation.error]);

  // Focus price input if needed with improved mechanism
  useEffect(() => {
    if (shouldFocusPrice && !isSuccess) {
      setIsPriceAdjustMode(true);
    }
  }, [shouldFocusPrice, isSuccess]);

  // Reset price adjust mode if success changes
  useEffect(() => {
    if (isSuccess) {
      setIsPriceAdjustMode(false);
    }
  }, [isSuccess]);

  // Handle product not found error
  useEffect(() => {
    if (isEditMode && !isLoadingListing && existingListing?.code === 'entity_not_found') {
      setIsProductNotFoundDialogOpen(true);
    }
  }, [isEditMode, isLoadingListing, existingListing?.code]);

  const handleImageUpload = async (index, setUploadError) => {
    // Clear any previous error when user selects a new image
    if (setUploadError) {
      setUploadError(null);
    }

    try {
      // Show file picker
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = 'image/jpeg,image/jpg,image/webp';
      
      input.onchange = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        
        try {
          // Validate the image before uploading
          const validation = await validateImage(file);
          if (!validation.isValid) {
            setUploadError(validation.error);
            return;
          }
          
          // First step: Get upload URL
          const fileInfo = {
            filename: encodeURIComponent(file.name),
            contentType: file.type,
            contentLength: file.size
          };
          
          const uploadInfo = await getUploadUrl(fileInfo);
          
          if (!uploadInfo?.data) {
            setUploadError('Failed to get upload URL');
            return;
          }
          
          const { url, method, image } = uploadInfo.data;
          
          // Second step: Upload the file
          await uploadFile(url, method, file);
          
          // Update images with the image data (now contains key and url)
          // Using the callback to ensure we're working with the latest state
          setImages(prevImages => {
            const newImages = { ...prevImages };
            
            if (internalCardType === 'PSA_SINGLE_CARD') {
              if (index < 2) {
                // For PSA cards: first 2 images are front and back
                const keyName = ['front', 'back'][index];
                newImages[keyName] = image;
              } else {
                // For PSA cards: additional images go to extras array
                const extraIndex = index - 2;
                if (extraIndex >= newImages.extras.length) {
                  // If adding a new extra image
                  newImages.extras = [...newImages.extras, image];
                } else {
                  // If replacing an existing extra image
                  const updatedExtras = [...newImages.extras];
                  updatedExtras[extraIndex] = image;
                  newImages.extras = updatedExtras;
                }
              }
            } else {
              // For non-PSA cards: keep original logic
              if (index < 7) {
                // For the first 7 images, assign to specific position based on index
                const keyName = imageIndexToKey[index];
                newImages[keyName] = image;
              } else {
                // For additional images, add to extras array at the specific position
                const extraIndex = index - 7;
                if (extraIndex >= newImages.extras.length) {
                  // If adding a new extra image
                  newImages.extras = [...newImages.extras, image];
                } else {
                  // If replacing an existing extra image
                  const updatedExtras = [...newImages.extras];
                  updatedExtras[extraIndex] = image;
                  newImages.extras = updatedExtras;
                }
              }
            }
            
            return newImages;
          });
          
          // Clear any previous error on successful upload
          if (setUploadError) {
            setUploadError(null);
          }
        } catch (error) {
          console.error('Image upload error:', error);
          setUploadError(error.message || 'Failed to upload image');
        }
      };
      
      input.click();
    } catch (error) {
      console.error('Image upload error:', error);
      if (setUploadError) {
        setUploadError(error.message || 'Failed to upload image');
      }
    }
  };

  const handleImageDelete = (index) => {
    setImages(prevImages => {
      const newImages = { ...prevImages };
      
      if (internalCardType === 'PSA_SINGLE_CARD') {
        if (index < 2) {
          // For PSA cards: first 2 images are front and back
          const keyName = ['front', 'back'][index];
          newImages[keyName] = null;
        } else {
          // For PSA cards: remove from extras array
          const extraIndex = index - 2;
          if (extraIndex < newImages.extras.length) {
            const updatedExtras = [...newImages.extras];
            updatedExtras.splice(extraIndex, 1);
            newImages.extras = updatedExtras;
          }
        }
      } else {
        // For non-PSA cards: keep original logic
        if (index < 7) {
          // For the first 7 images, set the specific key to null
          const keyName = imageIndexToKey[index];
          newImages[keyName] = null;
        } else {
          // For extra images, remove from extras array
          const extraIndex = index - 7;
          if (extraIndex < newImages.extras.length) {
            const updatedExtras = [...newImages.extras];
            updatedExtras.splice(extraIndex, 1);
            newImages.extras = updatedExtras;
          }
        }
      }
      
      return newImages;
    });
  };

  const handleImagePreview = (index) => {
    let imageUrl;
    
    if (internalCardType === 'PSA_SINGLE_CARD') {
      if (index < 2) {
        // For PSA cards: first 2 images are front and back
        const keyName = ['front', 'back'][index];
        imageUrl = images[keyName]?.url;
      } else {
        // For PSA cards: extra images
        const extraIndex = index - 2;
        if (extraIndex < images.extras.length) {
          imageUrl = images.extras[extraIndex].url;
        }
      }
    } else {
      // For non-PSA cards: keep original logic
      if (index < 7) {
        // For the first 7 images
        const keyName = imageIndexToKey[index];
        imageUrl = images[keyName]?.url;
      } else {
        // For extra images
        const extraIndex = index - 7;
        if (extraIndex < images.extras.length) {
          imageUrl = images.extras[extraIndex].url;
        }
      }
    }
    
    // Show image in dialog if it exists
    if (imageUrl) {
      setPreviewImage(imageUrl);
      setPreviewDialogOpen(true);
    }
  };

  // Check if all required images are uploaded for non-PSA cards
  const areRequiredImagesUploaded = () => {
    if (internalCardType !== 'NON_PSA_SINGLE_CARD') {
      return true;
    }
    
    // Only front and back images are required for non-PSA listings
    return images.front !== null && images.back !== null;
  };


  const performSubmission = () => {
    if (!ensureActionAllowed()) {
      return;
    }

    // Prepare listing data based on card type
    const data = {
      type: internalCardType,
      productId: id,
      price: price,
    };

    // Add the appropriate field based on card type
    if (internalCardType === 'PSA_SINGLE_CARD') {
      data.psa = grade;
      
      // Add PSA images (front, back, and extras - all optional)
      const formattedImages = {};
      if (images.front) formattedImages.front = images.front.key;
      if (images.back) formattedImages.back = images.back.key;
      
      // Add extra images
      if (images.extras.length > 0) {
        formattedImages.extras = images.extras.map(img => img.key);
      }
      
      data.images = formattedImages;
    } else if (internalCardType === 'NON_PSA_SINGLE_CARD') {
      data.condition = condition;
      data.description = description || "";
      
      // Add images if they exist
      if (areRequiredImagesUploaded()) {
        // Format images to only include keys
        const formattedImages = {};
        
        // Add main images
        imageIndexToKey.forEach(key => {
          if (images[key]) {
            formattedImages[key] = images[key].key;
          }
        });
        
        // Add extra images
        if (images.extras.length > 0) {
          formattedImages.extras = images.extras.map(img => img.key);
        }
        
        data.images = formattedImages;
      }
    } else if (internalCardType === 'CARD_BOX') {
      data.bundledQuantity = quantity;
    }

    // Submit the listing - either create or update
    if (isEditMode) {
      data.productId = undefined;
      updateListingMutation.mutate(data);
    } else {
      createListingMutation.mutate(data);
    }
  };

  const handleSubmit = () => {
    trackEvent('listitem_confirm_click');

    // Check if user has a verified phone
    if (!userProfile?.data?.phone) {
      setPhoneDialogCallback(() => () => handleSubmit());
      setIsPhoneDialogOpen(true);
      return;
    }

    // Check if user has any addresses
    if (!userAddresses?.data || userAddresses.data.length === 0) {
      setAddressEditorCallback(() => () => performSubmission());
      setIsAddressEditorOpen(true);
      return;
    }

    performSubmission();
  };

  // Handle phone dialog close
  const handlePhoneDialogClose = (success) => {
    setIsPhoneDialogOpen(false);
    
    // If binding was successful and we have a callback, execute it
    if (success && phoneDialogCallback) {
      phoneDialogCallback();
    }
    setPhoneDialogCallback(null);
  };

  // Handle address editor close and save
  const handleAddressEditorClose = () => {
    setIsAddressEditorOpen(false);
    setAddressEditorCallback(null);
  };

  const handleAddressEditorSave = async (addressData) => {
    try {
      await createShippingAddress(addressData);
      
      // Refetch addresses to update the list
      await queryClient.invalidateQueries(['user-addresses']);

      setIsAddressEditorOpen(false);
      
      // If we have a callback (meaning user was trying to submit), execute it
      if (addressEditorCallback) {
        addressEditorCallback();
      }
    } catch (error) {
      console.error('Failed to create address:', error);
      // Handle error (could add toast notification here)
    }
    setAddressEditorCallback(null);
  };

  const handleShowCancelConfirm = (id) => {
    setListingToCancel(id);
    setIsConfirmDialogOpen(true);
  };

  const closeConfirmDialog = () => {
    setIsConfirmDialogOpen(false);
    setListingToCancel(null);
  };

  const confirmCancelListing = () => {
    if (listingToCancel) {
      cancelListingMutation.mutate(listingToCancel);
    }
    setIsConfirmDialogOpen(false);
    setListingToCancel(null);
  };

  // Check if this is a first-time listing with reward
  const isFirstTimeListing = existingListing?.data?.benefitReward?.issueReward || listingData?.benefitReward?.issueReward;
  const rewardAmount = existingListing?.data?.benefitReward?.rewardAmount || listingData?.benefitReward?.rewardAmount;

  const handleCancel = () => {
    trackEvent('cancel_listing_click');

    if (isEditMode && listingId) {
      handleShowCancelConfirm(listingId);
    } else {
      // Just navigate back if not in edit mode
      navigate('/me/listings');
    }
  };

  const handleCancelListing = (listingId) => {
    handleShowCancelConfirm(listingId);
  };

  const handleAdjust = (listingId) => {
    setIsSuccess(false);
    navigate(`/listing/edit/${listingId}`);
  };

  const handleListAnother = () => {
    // Reset form state
    setIsSuccess(false);
    setListingData(null);
    setGrade('PSA_10');
    setCondition('MINT');
    setDescription('');
    setPrice(0);
    setQuantity(1);
    setImages({
      front: null,
      back: null,
      frontTopLeft: null,
      frontTopRight: null,
      frontBottomLeft: null,
      frontBottomRight: null,
      damage: null,
      extras: []
    });
    
    // If in edit mode, navigate to create mode
    if (isEditMode) {
      navigate(`/card/${listingData.product.id}/sell`);
    }
  };

  // Handle card type changes (PSA vs non-PSA)
  const handleCardTypeChange = (newType) => {
    if (newType === 'NON_PSA_SINGLE_CARD') {
      // Change to non-PSA mode - add NON_PSA parameter to URL
      const searchParams = new URLSearchParams(location.search);
      searchParams.set('NON_PSA', '1');
      navigate(`/card/${id}/sell?${searchParams.toString()}`, { replace: true });
      setInternalCardType('NON_PSA_SINGLE_CARD');
    } else if (newType === 'PSA_SINGLE_CARD') {
      // Change to PSA mode - remove NON_PSA parameter from URL
      const searchParams = new URLSearchParams(location.search);
      searchParams.delete('NON_PSA');
      navigate(`/card/${id}/sell?${searchParams.toString()}`, { replace: true });
      setInternalCardType('PSA_SINGLE_CARD');
    }
  };

  // Display loading state while fetching data
  if (isLoadingCard || isLoadingStats || (isEditMode && isLoadingListing) || isLoadingProfile || isLoadingAddresses) {
    return (
      <div className="container py-8 flex justify-center">
        <p>{t('common.loading')}...</p>
      </div>
    );
  }

  const displayCard = cardInfo?.data || existingListing?.data?.product;

  const handleProductNotFoundConfirm = () => {
    setIsProductNotFoundDialogOpen(false);
    navigate(-1);
  };
  // Format images for the card section
  const flattenedImages = (() => {
    if (internalCardType === 'PSA_SINGLE_CARD') {
      // For PSA cards: front, back, then only non-null extras (packed)
      const packedExtras = images.extras.filter(img => img !== null && img !== undefined);
      return [
        images.front,
        images.back,
        ...packedExtras
      ];
    } else {
      // For non-PSA cards: keep original structure
      return [
        images.front,
        images.back, 
        images.frontTopLeft,
        images.frontTopRight,
        images.frontBottomLeft,
        images.frontBottomRight,
        images.damage,
        ...images.extras
      ];
    }
  })();

  if (isPenaltyStatusFetched && penaltyStatus !== USER_STATUS.NORMAL) {
    return (
      <>
        <div className="min-h-screen w-full bg-[#f7f7f7]" />
        <PenaltyRestrictionDialog {...penaltyDialogProps} />
      </>
    );
  }

  return (
    <>
      {!isSuccess ? (
          <div className='bg-[#f7f7f7] flex-1'>
              <div className="container py-8 flex justify-center grid grid-cols-12 gap-6">
                  <div className="col-span-8">
                      <div className="space-y-6">
                      <CardTypeSection
                        card={displayCard}
                        cardStats={cardStats?.data}
                        internalCardType={internalCardType}
                        grade={grade}
                        condition={condition}
                        quantity={quantity}
                      />
                      
                      <CardSellInfoSection
                          type={internalCardType}
                          grade={grade}
                          onGradeChange={setGrade}
                          condition={condition}
                          onConditionChange={setCondition}
                          description={description}
                          onDescriptionChange={(e) => setDescription(e.target.value)}
                          price={price}
                          onPriceChange={(e) => setPrice(Number(e.target.value))}
                          priceInputRef={priceInputRef}
                          isPriceAdjustMode={isPriceAdjustMode}
                          quantity={quantity}
                          onQuantityChange={(e) => setQuantity(Number(e.target.value))}
                          images={flattenedImages}
                          onImageUpload={handleImageUpload}
                          onImageDelete={handleImageDelete}
                          onImagePreview={handleImagePreview}
                          cardStats={cardStats?.data}
                          onTypeChange={handleCardTypeChange}
                          isEditMode={isEditMode}
                      />
                      </div>
                  </div>
                  
                  <div className="col-span-4">
                      <PriceEstimateSection
                          price={price}
                          quantity={quantity}
                          type={internalCardType}
                          productId={id || existingListing?.data?.product?.id}
                          boxType={displayCard?.boxType}
                          onSubmit={handleSubmit}
                          isLoading={isEditMode ? updateListingMutation.isPending : createListingMutation.isPending}
                          isEditMode={isEditMode}
                          onCancel={handleCancel}
                          isFormValid={(internalCardType !== 'NON_PSA_SINGLE_CARD' || areRequiredImagesUploaded()) && price >= 1000}
                          />
                  </div>
              </div>
          </div>
      ) : (
          <div className='container py-8 flex justify-center items-center'>
              <SuccessSection
                  listingData={listingData}
                  card={displayCard}
                  cardType={internalCardType}
                  onCancel={handleCancel}
                  onCancelListing={handleCancelListing}
                  onAdjust={handleAdjust}
                  onListAnother={handleListAnother}
                  isEditMode={isEditMode}
              />
          </div>
      )}
      
      {previewDialogOpen && (
        <div className="fixed inset-0 z-50 bg-black/90">
          <div className="w-full h-full">
            <ImagePreviewWithRequest
              images={[previewImage]}
              initialIndex={0}
              onClose={() => setPreviewDialogOpen(false)}
              showRequestFeature={false}
              className="bg-black"
            />
          </div>
        </div>
      )}

      {/* Cancel Listing Confirmation Dialog */}
      <ConfirmDialog
        isOpen={isConfirmDialogOpen}
        onClose={closeConfirmDialog}
        onConfirm={confirmCancelListing}
        title={t('listings.cancelListing.first_time_title')}
        description={isFirstTimeListing ? t('listings.cancelListing.first_time_description') : t('listings.cancelListing.description')}
        cancelText={t('listings.cancelListing.first_time_cancel')}
        confirmText={t('listings.cancelListing.confirm')}
      />

      {/* Product Not Found Dialog */}
      <ConfirmDialog
        isOpen={isProductNotFoundDialogOpen}
        onClose={handleProductNotFoundConfirm}
        onConfirm={handleProductNotFoundConfirm}
        title="商品が見つかりません"
        confirmText="戻る"
      />

      {/* Phone Verification Dialog */}
      {isPhoneDialogOpen && (
        <BindPhoneDialog onClose={handlePhoneDialogClose} />
      )}

      {/* Address Editor Dialog */}
      <AddressEditor
        isOpen={isAddressEditorOpen}
        onClose={handleAddressEditorClose}
        onSave={handleAddressEditorSave}
        isEditing={false}
        hideDefaultOption={true}
      />
      <PenaltyRestrictionDialog {...penaltyDialogProps} />
    </>
  );
};

export default CreateListingPage; 
